import { ExternalLink, MessagesSquare, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { NavLink, useParams, useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useVendorPages } from "@/hooks/useVendorPages";
import type { VendorPage } from "@/lib/api";

export default function ChatsPage() {
  const { vendorId } = useParams<{ vendorId?: string }>();
  const pagesQ = useVendorPages();

  if (pagesQ.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-fg">
        Loading chat rooms…
      </div>
    );
  }
  if (pagesQ.isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-400">
        Failed to load chat rooms: {(pagesQ.error as Error).message}
      </div>
    );
  }

  const rooms = (pagesQ.data ?? []).filter((page) =>
    ["chat_room", "room"].includes(page.page_type),
  );
  return <ChatsDirectory rooms={rooms} vendorId={vendorId} />;
}

function ChatsDirectory({
  rooms,
  vendorId,
}: {
  rooms: VendorPage[];
  vendorId?: string;
}) {
  const [query, setQuery] = useState("");
  const [searchParams] = useSearchParams();
  const vendorFilter = useMemo(
    () => new Set(vendorId ? [vendorId] : csvParam(searchParams, "vendors")),
    [searchParams, vendorId],
  );
  const capabilityFilter = useMemo(
    () => new Set(csvParam(searchParams, "capabilities")),
    [searchParams],
  );
  const visibleRooms = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rooms.filter((room) => {
      if (vendorFilter.size > 0 && !vendorFilter.has(room.vendor)) return false;
      if (
        capabilityFilter.size > 0 &&
        !Array.from(capabilityFilter).every((capability) => hasCapability(room, capability))
      ) {
        return false;
      }
      if (!needle) return true;
      return [
        room.id,
        room.label,
        room.nav_label,
        room.vendor_display_name,
        room.subscription,
        room.section,
        room.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [capabilityFilter, query, rooms, vendorFilter]);
  const vendors = groupByVendor(visibleRooms);
  const activeVendor = vendorId ? rooms.find((room) => room.vendor === vendorId) : null;
  const activeFilters = filterLabels({
    vendors: vendorFilter,
    capabilities: capabilityFilter,
  });

  return (
    <div className="flex min-h-full flex-col">
      <header className="border-b border-border px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-muted-fg">
              <MessagesSquare className="size-4" aria-hidden />
              <span>{vendorId ? activeVendor?.vendor_display_name ?? vendorId : "All vendors"}</span>
            </div>
            <h1 className="mt-1 text-xl font-semibold text-fg">
              {vendorId ? "Vendor chat rooms" : "Chat rooms"}
            </h1>
            <p className="mt-1 text-sm text-muted-fg">
              Vendor room links and focused chat popouts from the canonical vendor page catalog.
            </p>
            {activeFilters.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {activeFilters.map((filter) => (
                  <Badge key={filter} variant="outline">
                    {filter}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-fg" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search chat rooms"
              className="h-9 w-full rounded-md border border-input bg-bg pl-9 pr-3 text-sm text-fg outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>
      </header>
      <div className="flex-1 overflow-auto px-4 py-4 sm:px-6">
        {vendors.length === 0 ? (
          <div className="rounded-md border border-border bg-muted/5 p-4 text-sm text-muted-fg">
            No chat rooms match this view.
          </div>
        ) : (
          <div className="space-y-6">
            {vendors.map((vendor) => (
              <section key={vendor.id} className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-semibold text-fg">{vendor.label}</h2>
                  {!vendorId ? (
                    <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
                      <NavLink to={`/chats/${vendor.id}`}>Vendor view</NavLink>
                    </Button>
                  ) : null}
                  <span className="text-xs text-muted-fg">{vendor.rooms.length} rooms</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {vendor.rooms.map((room) => (
                    <RoomCard key={room.id} room={room} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RoomCard({ room }: { room: VendorPage }) {
  return (
    <article className="flex min-h-32 flex-col rounded-md border border-border bg-muted/5 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-medium text-fg">{displayLabel(room)}</h3>
            <p className="mt-1 truncate text-xs text-muted-fg">
              {room.subscription.replace(/_/g, " ")}
              {room.section ? ` · ${room.section.replace(/_/g, " ")}` : ""}
            </p>
          </div>
          <Badge variant={room.status === "active" ? "success" : "outline"}>
            {room.status}
          </Badge>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Badge variant="secondary">{room.page_type.replace(/_/g, " ")}</Badge>
          {room.focused_url ? <Badge variant="outline">focused chat</Badge> : null}
          {room.capabilities.monitor_activity ? <Badge variant="outline">monitor</Badge> : null}
        </div>
        {room.notes ? (
          <p className="mt-3 line-clamp-2 text-xs text-muted-fg">{room.notes}</p>
        ) : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button asChild size="sm" variant="outline" className="h-8 px-2.5 text-xs">
          <a href={room.url} target="_blank" rel="noreferrer">
            <ExternalLink className="size-3.5" />
            Full room
          </a>
        </Button>
        {room.focused_url ? (
          <Button
            size="sm"
            className="h-8 px-2.5 text-xs"
            onClick={() => openChatWindow(room)}
          >
            <MessagesSquare className="size-3.5" />
            Chat
          </Button>
        ) : null}
      </div>
    </article>
  );
}

function displayLabel(vendorPage: VendorPage): string {
  return vendorPage.nav_label ?? vendorPage.label;
}

function openChatWindow(room: VendorPage): void {
  if (!room.focused_url) return;
  window.open(
    room.focused_url,
    `signals-chat-${room.id}`,
    "popup=yes,width=520,height=760,resizable=yes,scrollbars=yes",
  );
}

function groupByVendor(rooms: VendorPage[]): Array<{
  id: string;
  label: string;
  rooms: VendorPage[];
}> {
  const groups = new Map<string, { id: string; label: string; rooms: VendorPage[] }>();
  rooms.forEach((room) => {
    const existing = groups.get(room.vendor);
    if (existing) {
      existing.rooms.push(room);
      return;
    }
    groups.set(room.vendor, {
      id: room.vendor,
      label: room.vendor_display_name || room.vendor,
      rooms: [room],
    });
  });
  return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function csvParam(searchParams: URLSearchParams, key: string): string[] {
  return searchParams
    .get(key)
    ?.split(",")
    .map((value) => value.trim())
    .filter(Boolean) ?? [];
}

function hasCapability(room: VendorPage, capability: string): boolean {
  return Boolean(room.capabilities[capability as keyof VendorPage["capabilities"]]);
}

function filterLabels(filters: Record<string, Set<string>>): string[] {
  return Object.entries(filters).flatMap(([name, values]) =>
    Array.from(values).map((value) => `${name}: ${value}`),
  );
}
